// Wizualizacja procesu gięcia - przekrój 2D i animacja 3D
import React, { useRef, useEffect, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Box, Text, Line } from '@react-three/drei';
import * as THREE from 'three';
import { BendingPhysics } from './BendingPhysics';
import { findMaterial } from './BendingDatabase';

interface BendingVisualizationProps {
  materialId: string;
  thickness: number;
  bendAngle: number;
  bendRadius: number;
  upperTemp: number;
  lowerTemp: number;
  heatingTime: number;
  heatingZoneWidth: number;
  isAnimating?: boolean;
}

// Komponent 3D dla animacji gięcia
const BendingAnimation: React.FC<{
  thickness: number;
  bendAngle: number;
  bendRadius: number;
  heatingZoneWidth: number;
  isAnimating: boolean;
  materialColor: string;
}> = ({ thickness, bendAngle, bendRadius, heatingZoneWidth, isAnimating, materialColor }) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const [currentAngle, setCurrentAngle] = useState(0);
  
  useFrame((state, delta) => {
    if (!isAnimating || !meshRef.current) return;
    
    // Animacja gięcia
    const targetAngle = (bendAngle * Math.PI) / 180;
    const newAngle = currentAngle + delta * 0.5;
    
    if (newAngle < targetAngle) {
      setCurrentAngle(newAngle);
      
      // Deformacja geometrii dla symulacji gięcia
      const geometry = meshRef.current.geometry as THREE.BoxGeometry;
      const positions = geometry.attributes.position;
      
      for (let i = 0; i < positions.count; i++) {
        const x = positions.getX(i);
        const y = positions.getY(i);
        const z = positions.getZ(i);
        
        // Gięcie wokół osi Z
        if (x > -heatingZoneWidth / 200 && x < heatingZoneWidth / 200) {
          const bendFactor = Math.sin((x + heatingZoneWidth / 200) * Math.PI / (heatingZoneWidth / 100));
          const newY = y * Math.cos(newAngle * bendFactor) - z * Math.sin(newAngle * bendFactor);
          const newZ = y * Math.sin(newAngle * bendFactor) + z * Math.cos(newAngle * bendFactor);
          
          positions.setY(i, newY);
          positions.setZ(i, newZ);
        }
      }
      
      positions.needsUpdate = true;
      geometry.computeVertexNormals();
    }
  });
  
  // Reset animacji
  useEffect(() => {
    if (!isAnimating) {
      setCurrentAngle(0);
      if (meshRef.current) {
        const geometry = meshRef.current.geometry as THREE.BoxGeometry;
        geometry.dispose();
        meshRef.current.geometry = new THREE.BoxGeometry(4, thickness / 10, 1);
      }
    }
  }, [isAnimating, thickness]);
  
  return (
    <>
      {/* Płyta do gięcia */}
      <Box
        ref={meshRef}
        args={[4, thickness / 10, 1]}
        position={[0, 0, 0]}
      >
        <meshStandardMaterial 
          color={materialColor}
          transparent
          opacity={0.8}
          side={THREE.DoubleSide}
        />
      </Box>
      
      {/* Strefa grzania - wizualizacja */}
      <Box
        args={[heatingZoneWidth / 100, 0.01, 1.2]}
        position={[0, thickness / 20 + 0.1, 0]}
      >
        <meshStandardMaterial 
          color="#ff6b6b"
          transparent
          opacity={0.3}
          emissive="#ff0000"
          emissiveIntensity={0.5}
        />
      </Box>
      
      <Box
        args={[heatingZoneWidth / 100, 0.01, 1.2]}
        position={[0, -thickness / 20 - 0.1, 0]}
      >
        <meshStandardMaterial 
          color="#ff6b6b"
          transparent
          opacity={0.3}
          emissive="#ff0000"
          emissiveIntensity={0.5}
        />
      </Box>
      
      {/* Etykiety */}
      <Text
        position={[0, thickness / 20 + 0.3, 0]}
        fontSize={0.15}
        color="#666"
      >
        Grzałka górna
      </Text>
      
      <Text
        position={[0, -thickness / 20 - 0.3, 0]}
        fontSize={0.15}
        color="#666"
      >
        Grzałka dolna
      </Text>
    </>
  );
};

// Komponent przekroju 2D z rozkładem temperatury
const TemperatureCrossSection: React.FC<{
  materialId: string;
  thickness: number;
  upperTemp: number;
  lowerTemp: number;
  heatingTime: number;
}> = ({ materialId, thickness, upperTemp, lowerTemp, heatingTime }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [temperatureDistribution, setTemperatureDistribution] = useState<number[]>([]);
  
  useEffect(() => {
    const material = findMaterial(materialId);
    if (!material) return;
    
    // Oblicz rozkład temperatury
    const distribution = BendingPhysics.calculateTemperatureDistribution(
      material,
      thickness,
      upperTemp,
      lowerTemp,
      heatingTime
    );
    
    setTemperatureDistribution(distribution);
  }, [materialId, thickness, upperTemp, lowerTemp, heatingTime]);
  
  useEffect(() => {
    if (!canvasRef.current || temperatureDistribution.length === 0) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Wyczyść canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    const width = canvas.width;
    const height = canvas.height;
    const padding = 40;
    const graphWidth = width - 2 * padding;
    const graphHeight = height - 2 * padding;
    
    // Rysuj tło
    ctx.fillStyle = '#18181b'; // zinc-900
    ctx.fillRect(0, 0, width, height);
    
    // Rysuj osie
    ctx.strokeStyle = '#71717a'; // zinc-500
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(padding, padding);
    ctx.lineTo(padding, height - padding);
    ctx.lineTo(width - padding, height - padding);
    ctx.stroke();
    
    // Etykiety osi
    ctx.fillStyle = '#d4d4d8'; // zinc-300
    ctx.font = '12px sans-serif';
    ctx.fillText('Temperatura (°C)', padding - 35, padding - 10);
    ctx.fillText('Grubość (mm)', width / 2 - 30, height - 10);
    
    // Znajdź min i max temperatury
    const maxTemp = Math.max(...temperatureDistribution, upperTemp, lowerTemp);
    const minTemp = Math.min(...temperatureDistribution, 20);
    const tempRange = maxTemp - minTemp;
    
    // Rysuj gradient temperatury
    const layerHeight = graphHeight / temperatureDistribution.length;
    
    temperatureDistribution.forEach((temp, index) => {
      const x = padding;
      const y = padding + index * layerHeight;
      const barWidth = (temp - minTemp) / tempRange * graphWidth;
      
      // Kolor zależny od temperatury
      const hue = 240 - (temp - minTemp) / tempRange * 240; // Od niebieskiego do czerwonego
      ctx.fillStyle = `hsl(${hue}, 70%, 50%)`;
      ctx.fillRect(x, y, barWidth, layerHeight);
      
      // Wartość temperatury
      if (index % 2 === 0) {
        ctx.fillStyle = '#e4e4e7'; // zinc-200
        ctx.font = '10px sans-serif';
        ctx.fillText(`${Math.round(temp)}°C`, x + barWidth + 5, y + layerHeight / 2);
      }
    });
    
    // Rysuj linie graniczne dla grzałek
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    
    // Linia górnej grzałki
    ctx.beginPath();
    const upperLineX = padding + (upperTemp - minTemp) / tempRange * graphWidth;
    ctx.moveTo(upperLineX, padding);
    ctx.lineTo(upperLineX, padding + layerHeight * 2);
    ctx.stroke();
    ctx.fillStyle = '#ef4444';
    ctx.font = '11px sans-serif';
    ctx.fillText(`Górna: ${upperTemp}°C`, upperLineX - 40, padding - 5);
    
    // Linia dolnej grzałki
    ctx.beginPath();
    const lowerLineX = padding + (lowerTemp - minTemp) / tempRange * graphWidth;
    ctx.moveTo(lowerLineX, height - padding - layerHeight * 2);
    ctx.lineTo(lowerLineX, height - padding);
    ctx.stroke();
    ctx.fillText(`Dolna: ${lowerTemp}°C`, lowerLineX - 40, height - padding + 15);
    
    ctx.setLineDash([]);
    
    // Skala temperatury
    ctx.fillStyle = '#a1a1aa'; // zinc-400
    ctx.font = '10px sans-serif';
    for (let i = 0; i <= 5; i++) {
      const temp = minTemp + (tempRange * i) / 5;
      const x = padding + (graphWidth * i) / 5;
      ctx.fillText(`${Math.round(temp)}`, x - 10, height - padding + 15);
      
      // Linie siatki
      ctx.strokeStyle = '#3f3f46'; // zinc-700
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(x, padding);
      ctx.lineTo(x, height - padding);
      ctx.stroke();
    }
    
    // Skala grubości
    for (let i = 0; i <= temperatureDistribution.length; i += 2) {
      const y = padding + (i * graphHeight) / temperatureDistribution.length;
      const depth = (i * thickness) / temperatureDistribution.length;
      ctx.fillText(`${depth.toFixed(1)}`, padding - 30, y + 3);
    }
    
  }, [temperatureDistribution, upperTemp, lowerTemp, thickness]);
  
  return (
    <div className="bg-zinc-800 rounded-lg shadow-xl p-4 border border-zinc-700">
      <h3 className="text-sm font-semibold text-gray-200 mb-2">
        Rozkład temperatury w przekroju
      </h3>
      <canvas 
        ref={canvasRef}
        width={400}
        height={250}
        className="w-full max-w-md mx-auto rounded bg-zinc-900"
      />
      <div className="mt-2 text-xs text-gray-400 text-center">
        Gradient temperatury w materiale podczas nagrzewania
      </div>
    </div>
  );
};

// Główny komponent wizualizacji
export const BendingVisualization: React.FC<BendingVisualizationProps> = ({
  materialId,
  thickness,
  bendAngle,
  bendRadius,
  upperTemp,
  lowerTemp,
  heatingTime,
  heatingZoneWidth,
  isAnimating = false
}) => {
  const material = findMaterial(materialId);
  
  // Określ kolor materiału
  const getMaterialColor = () => {
    if (!material) return '#888888';
    switch (material.type) {
      case 'PMMA_XT':
      case 'PMMA_GS':
        return '#e3f2fd'; // Jasnoniebieski dla plexi
      case 'PC':
        return '#f3e5f5'; // Jasnofioletowy dla poliwęglanu
      case 'PETG':
        return '#e8f5e9'; // Jasnozielony dla PETG
      case 'PVC_FOAM':
        return '#fff3e0'; // Jasnopomarańczowy dla PVC
      default:
        return '#f5f5f5';
    }
  };
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Wizualizacja 3D */}
      <div className="bg-zinc-800 rounded-lg shadow-xl p-4 border border-zinc-700">
        <h3 className="text-sm font-semibold text-gray-200 mb-2">
          Symulacja procesu gięcia
        </h3>
        <div className="h-64 lg:h-80 bg-zinc-900 rounded">
          <Canvas camera={{ position: [5, 3, 5], fov: 50 }}>
            <ambientLight intensity={0.5} />
            <pointLight position={[10, 10, 10]} />
            <OrbitControls enablePan={false} />
            <BendingAnimation
              thickness={thickness}
              bendAngle={bendAngle}
              bendRadius={bendRadius}
              heatingZoneWidth={heatingZoneWidth}
              isAnimating={isAnimating}
              materialColor={getMaterialColor()}
            />
            <gridHelper args={[10, 10]} />
          </Canvas>
        </div>
        <div className="mt-2 flex items-center justify-center space-x-4 text-xs text-gray-400">
          <span>Kąt: {bendAngle}°</span>
          <span>•</span>
          <span>Strefa grzania: {heatingZoneWidth}mm</span>
          <span>•</span>
          <span>Promień: {bendRadius}mm</span>
        </div>
      </div>
      
      {/* Przekrój temperatury */}
      <TemperatureCrossSection
        materialId={materialId}
        thickness={thickness}
        upperTemp={upperTemp}
        lowerTemp={lowerTemp}
        heatingTime={heatingTime}
      />
    </div>
  );
};

export default BendingVisualization;